<template>
    <v-card>
        <v-card-title class="d-flex align-center ga-2">
            <span>{{ title }}</span>
            <slot name="title-append" />
            <v-spacer />
            <span v-if="!loading" class="text-caption text-medium-emphasis text-data">
                {{ total.toLocaleString() }}
            </span>
        </v-card-title>
        <v-divider />
        <v-skeleton-loader v-if="loading && events.length === 0" type="list-item-two-line@4" />
        <EmptyState
            v-else-if="events.length === 0"
            icon="mdi-timeline-text-outline"
            :title="emptyTitle"
            :text="emptyText"
        />
        <template v-else>
            <div class="px-4">
                <ActivityEvent
                    v-for="event in events"
                    :key="event.id"
                    :event="event"
                    :show-actor="props.showActor"
                />
            </div>
            <div v-if="events.length < total" class="text-center pa-2">
                <v-btn variant="text" size="small" :loading="loading" @click="loadMore">
                    Load more
                </v-btn>
            </div>
        </template>
    </v-card>
</template>

<script setup lang="ts">
import { UserActivity, UserActivityEvent } from "@/api/users";
import { ref, watch } from "vue";
import EmptyState from "../common/EmptyState.vue";
import ActivityEvent from "./ActivityEvent.vue";

type Props = {
    /** Rebuilding this closure (a new filter, a different user) reloads the feed from page one. */
    load: (page: number, perPage: number) => Promise<UserActivity>;
    title: string;
    emptyTitle: string;
    emptyText: string;
    showActor?: boolean;
};

const props = withDefaults(defineProps<Props>(), { showActor: false });

const PER_PAGE = 25;

const events = ref<UserActivityEvent[]>([]);
const total = ref(0);
const page = ref(0);
const loading = ref(false);

async function fetchPage() {
    loading.value = true;
    try {
        const activity = await props.load(page.value, PER_PAGE);
        total.value = activity.total;
        events.value = page.value === 0 ? activity.events : [...events.value, ...activity.events];
    } finally {
        loading.value = false;
    }
}

async function loadMore() {
    page.value += 1;
    await fetchPage();
}

watch(
    () => props.load,
    async () => {
        page.value = 0;
        await fetchPage();
    },
    { immediate: true }
);
</script>
